import { ApiClient, PagedResults, ServiceResponse, ServiceResponseNoContent } from "../../../common";
import { PlatformSettings } from "../../../settings/platformSettings";
import CreateInviteDto from "../../dtos/in/createInviteDto";
import CreateUserDto from "../../dtos/in/createUserDto";
import UpdateInviteDto from "../../dtos/in/updateInviteDto";
import { InviteDto } from "../../dtos/out/inviteDto";
import ResetPasswordDto from "../../dtos/out/resetPasswordDto";
import UserContactDto from "../../dtos/out/userContactDto";
import UserDto from "../../dtos/out/userDto";
import { UserIdentityType } from "../../enums/userIdentityType";
import { IUsers } from "../interfaces/users";

export default class Users extends ApiClient implements IUsers {

    private _baseUrl: string;
    constructor(private platformSettings: PlatformSettings) {
        super();
        this._baseUrl = platformSettings.apiUrl + "/auth";
    }

    get baseUrl(): string {
        return this._baseUrl;
    }

    // Users
    public async createUser(dto: CreateUserDto, accessToken: string): Promise<ServiceResponse<UserDto>> {
        return await super.Post<CreateUserDto, UserDto>(this.baseUrl, "users", dto, accessToken);
    }
    public async deleteUser(userId: number, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Delete(this.baseUrl, `users/${userId}`, accessToken);
    }
    public async resetUserSecret(userId: number, accessToken: string): Promise<ServiceResponse<string>> {
        return await super.Post<string, string>(this.baseUrl, `users/${userId}/user-secret`, null, accessToken);
    }
    public async addUserIdentity(userId: number, identityType: UserIdentityType, externalId: string, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Put(this.baseUrl, `users/${userId}/identities/${identityType}/${externalId}`, null, accessToken);
    }
    public async removeUserIdentity(userId: number, identityType: UserIdentityType, externalId: string, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Delete(this.baseUrl, `users/${userId}/identities/${identityType}/${externalId}`, accessToken);
    }
    public async addUserAuthScope(userId: number, authScope: string, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Put(this.baseUrl, `users/${userId}/auth-scopes/${authScope}`, null, accessToken);
    }
    public async removeUserAuthScope(userId: number, authScope: string, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Delete(this.baseUrl, `users/${userId}/auth-scopes/${authScope}`, accessToken);
    }
    public async replaceUserAuthScopes(userId: number, authScope: string[], accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Put(this.baseUrl, `users/${userId}/auth-scopes`, authScope, accessToken);
    }
    public async addUserRole(userId: number, roleId: number, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Put(this.baseUrl, `users/${userId}/roles/${roleId}`, null, accessToken);
    }
    public async removeUserRole(userId: number, roleId: number, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Delete(this.baseUrl, `users/${userId}/roles/${roleId}`, accessToken);
    }
    public async updateUserForcePasswordReset(userId: number, forcePasswordReset: boolean, accessToken: string): Promise<ServiceResponse<UserDto>> {
        return await super.Put<number, UserDto>(this.baseUrl, `users/${userId}/force-password-reset/${forcePasswordReset}`, userId, accessToken);
    }
    public async updateUserCanSkipMfa(userId: number, canSkipMfa: boolean, accessToken: string): Promise<ServiceResponse<UserDto>> {
        return await super.Put<number, UserDto>(this.baseUrl, `users/${userId}/can-skip-mfa/${canSkipMfa}`, userId, accessToken);
    }
    public async getUserContactDetails(userId: number, accessToken: string): Promise<ServiceResponse<UserContactDto>> {
        return await super.Get<UserContactDto>(this.baseUrl, `users/${userId}/contact-details`, accessToken);
    }
    public async me(accessToken: string): Promise<ServiceResponse<UserDto>> {
        return await super.Get<UserDto>(this.baseUrl, "users/me", accessToken);
    }
    public async getUserById(userId: number, accessToken: string): Promise<ServiceResponse<UserDto>> {
        return await super.Get<UserDto>(this.baseUrl, `users/${userId}`, accessToken);
    }
    public async resetUserPassword(
        userId: number,
        appId: number = null,
        accessToken: string
    ): Promise<ServiceResponse<ResetPasswordDto>> {
        let endpoint = `users/${userId}/password`;

        if (appId != null) {
            endpoint += `?appId=${appId}`;
        }

        return await super.Post<number, ResetPasswordDto>(this.baseUrl, endpoint, userId, accessToken);
    }

    public async getUserByEmailAddress(emailAddress: string, accessToken: string): Promise<ServiceResponse<UserDto>> {
        return await super.Get<UserDto>(this.baseUrl, `users?emailAddress=${encodeURIComponent(emailAddress)}`, accessToken);
    }

    public async getUserByIdentity(identityType: UserIdentityType, externalId: string, accessToken: string): Promise<ServiceResponse<UserDto>> {
        return await super.Get<UserDto>(this.baseUrl, `/users/identities/${identityType}/${externalId}`, accessToken);
    }
    public async searchUsers(
        page: number,
        pageSize = 200,
        emailAddress: string = null,
        accessToken: string
    ): Promise<ServiceResponse<PagedResults<UserDto>>> {
        let endpoint = `users/search-results?page=${page}&pageSize=${pageSize}`;

        if (emailAddress != null) {
            endpoint += `&emailAddress=${emailAddress}`;
        }

        return await super.Get<PagedResults<UserDto>>(
            this.baseUrl,
            endpoint,
            accessToken
        );
    }

    // User Invites
    public async createUserInvite(createInviteDto: CreateInviteDto, accessToken: string): Promise<ServiceResponse<InviteDto>> {
        return await super.Post<CreateInviteDto, InviteDto>(this.baseUrl, "invites", createInviteDto, accessToken);
    }
    public async getUserInviteById(inviteId: number, accessToken: string): Promise<ServiceResponse<InviteDto>> {
        return await super.Get<InviteDto>(this.baseUrl, `invites/${inviteId}`, accessToken);
    }
    public async getUserInviteByEmail(email: string, accessToken: string): Promise<ServiceResponse<InviteDto>> {
        return await super.Get<InviteDto>(this.baseUrl, `invites?emailAddress=${encodeURIComponent(email)}`, accessToken);
    }
    public async deleteUserInvite(inviteId: number, accessToken: string): Promise<ServiceResponseNoContent> {
        return await super.Delete(this.baseUrl, `invites/${inviteId}`, accessToken);
    }
    public async updateUserInvite(inviteId: number, updateInviteDto: UpdateInviteDto, accessToken: string): Promise<ServiceResponse<InviteDto>> {
        return await super.Put<UpdateInviteDto, InviteDto>(this.baseUrl, `invites/${inviteId}`, updateInviteDto, accessToken);
    }
}

export { Users }